import { useNavigate } from "@tanstack/react-router";
import { Store, Trash2 } from "lucide-react";

import { Crumbs, DesktopShell } from "../../components/desktop/shell.tsx";
import {
  Button,
  Empty,
  ErrorBlock,
  Img,
  Price,
  QtyStepper,
  Spinner,
} from "../../components/ui.tsx";
import { useCart, useMe, useRemoveFromCart, useSetCartQuantity } from "../../api/queries.ts";
import { readPurchaseAs, readViewAs, writeIdentity } from "../../lib/identity.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 购物车
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **表格 + 右侧固定的金额汇总。**
 *    手机版是一张张卡片竖着排，金额条钉在屏幕底部 —— 窄屏上只能这样。
 *    网页上有横向空间：商品 / 单价 / 数量 / 小计 排成一行，
 *    一眼能横着比；金额汇总常驻在右边，改数量时**合计当场跟着变**，
 *    不用滚到底去看。
 *
 * ⚠️ 仍然**按供应商分组** —— 下单时是按供应商拆单的，
 *    不分组的话用户看到的是一车东西、结果收到好几个包裹好几张单。
 */
export function DesktopCart() {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const { data, isPending, error } = useCart(Boolean(me));
  const setQty = useSetCartQuantity();
  const remove = useRemoveFromCart();

  /** 🔴 预览「我卖的价」时不能结算 —— 那是客户看到的价。 */
  const previewing = readPurchaseAs() !== null && readViewAs() === "shop";

  if (!me)
    return (
      <DesktopShell>
        <Empty title="还没登录" hint="登录后可以把商品加进购物车" action={{ label: "去登录", to: "/login" }} compact />
      </DesktopShell>
    );
  if (isPending)
    return (
      <DesktopShell>
        <Spinner compact />
      </DesktopShell>
    );
  if (error)
    return (
      <DesktopShell>
        <ErrorBlock error={error} />
      </DesktopShell>
    );

  const groups = data?.groups ?? [];
  const count = groups.reduce((n, g) => n + g.lines.length, 0);

  if (count === 0)
    return (
      <DesktopShell>
        <Crumbs items={[{ label: "首页", to: "/" }, { label: "购物车" }]} />
        <Empty title="购物车是空的" hint="去挑点建材吧" action={{ label: "去逛逛", to: "/products" }} compact />
      </DesktopShell>
    );

  return (
    <DesktopShell>
      <Crumbs items={[{ label: "首页", to: "/" }, { label: "购物车" }]} />

      {/*
        ⚠️ 车里有下架或缺货的商品时不整页报错，只把有问题的那几行说出来 ——
           一个商品下架不该让整个购物车打不开。
      */}
      {data!.issues.length > 0 && (
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-5 py-3.5 text-[14px] text-amber-800">
          {data!.issues.map((i) => (
            <p key={i.skuId}>
              {i.title ? `「${i.title}」` : "有商品"}
              {i.reason}
            </p>
          ))}
        </div>
      )}

      <div className="grid grid-cols-[minmax(0,1fr)_320px] items-start gap-8">
        {/* ── 左：商品表格 ── */}
        <div className="space-y-5">
          {groups.map((g) => (
            <div key={g.supplierOrgId} className="overflow-hidden rounded-xl border border-border bg-card">
              <div className="flex items-center gap-2 border-b border-border bg-secondary/40 px-5 py-3 text-[13px] text-muted-foreground">
                <Store className="size-4" />
                {g.supplierName}
              </div>

              {/* ⚠️ 表头只在网页上有意义 —— 手机上四列挤不下，那边是卡片。 */}
              <div className="grid grid-cols-[minmax(0,1fr)_120px_140px_120px_40px] items-center gap-4 border-b border-border px-5 py-2.5 text-[13px] text-muted-foreground">
                <span>商品</span>
                <span className="text-right">单价</span>
                <span className="text-center">数量</span>
                <span className="text-right">小计</span>
                <span />
              </div>

              {g.lines.map((l) => (
                <div
                  key={l.skuId}
                  className="grid grid-cols-[minmax(0,1fr)_120px_140px_120px_40px] items-center gap-4 border-b border-border px-5 py-4 last:border-b-0"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <Img
                      src={l.imageUrl}
                      alt={l.title}
                      className="size-16 shrink-0 rounded-lg object-cover"
                    />
                    <div className="min-w-0">
                      <p className="line-clamp-2 text-[14px] leading-snug">{l.title}</p>
                      {!l.available && (
                        <p className="mt-1 text-[12px] text-destructive">
                          {l.stock === 0 ? "已售罄" : `库存仅剩 ${l.stock}`}
                        </p>
                      )}
                    </div>
                  </div>

                  <span className="text-right text-[14px] text-muted-foreground">
                    <Price cents={l.unitPrice} />
                  </span>

                  <div className="flex justify-center">
                    <QtyStepper
                      value={l.quantity}
                      max={Math.max(1, l.stock)}
                      onChange={(q) => setQty.mutate({ skuId: l.skuId, quantity: q })}
                    />
                  </div>

                  <span className="text-right">
                    <Price cents={l.subtotal} className="text-[15px] text-brand-600" />
                  </span>

                  {/* ⚠️ 删除放在行尾且只有图标 —— 网页上有 hover 提示，不用写字占地方。 */}
                  <button
                    className="flex size-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => remove.mutate(l.skuId)}
                    aria-label="删除"
                    title="从购物车移除"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* ── 右：金额汇总（常驻） ── */}
        <aside className="sticky top-24 rounded-xl border border-border bg-card p-5">
          <p className="mb-4 text-[15px] font-medium">合计</p>

          <dl className="space-y-2.5 text-[14px]">
            {/*
              🔴 **店铺优惠要单独列出来。**
                 不列的话用户不知道自己因为挂在这个商家名下省了多少 ——
                 而那正是他不去别处买的理由。
            */}
            {data!.totalShopDiscount > 0 && (
              <>
                <div className="flex justify-between text-muted-foreground">
                  <dt>商品原价</dt>
                  <dd>
                    <Price cents={data!.totalOriginal} />
                  </dd>
                </div>
                <div className="flex justify-between text-brand-600">
                  <dt>店铺优惠</dt>
                  <dd>
                    −<Price cents={data!.totalShopDiscount} />
                  </dd>
                </div>
              </>
            )}
            <div className="flex justify-between text-muted-foreground">
              <dt>货款</dt>
              <dd>
                <Price cents={data!.totalGoods} />
              </dd>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <dt>运费</dt>
              <dd>
                <Price cents={data!.totalFreight} />
              </dd>
            </div>
          </dl>

          <div className="mt-4 flex items-baseline justify-between border-t border-border pt-4">
            <span className="text-[14px]">应付</span>
            <Price cents={data!.totalPayable} className="text-[24px] text-brand-600" />
          </div>

          <Button
            size="lg"
            className="mt-5 w-full"
            onClick={previewing ? () => writeIdentity("") : () => navigate({ to: "/checkout" })}
          >
            {previewing ? "按我的进货价结算" : `去结算（${count} 件）`}
          </Button>

          {groups.length > 1 && (
            /* ⚠️ 拆单要提前说 —— 收到好几张单才发现的话，用户会以为下重了。 */
            <p className="mt-3 text-[12px] leading-relaxed text-muted-foreground">
              这一车来自 {groups.length} 个供应商，提交后会拆成 {groups.length} 张订单分别发货。
            </p>
          )}
        </aside>
      </div>
    </DesktopShell>
  );
}
